import { useEffect, useRef, type ReactNode } from 'react';
import { ChevronRight, LifeBuoy, X } from 'lucide-react';

export interface HelpOverlayShellProps {
  /** Renders nothing when false. Never set from anything but a user action. */
  open: boolean;
  contextLabel: string;
  /** Page label when help was opened from a section; null at page level. */
  parentLabel?: string | null;
  onParentClick?: () => void;
  onClose: () => void;
  faqPane: ReactNode;
  chatPane: ReactNode;
}

/**
 * The two-pane, right-anchored slide-over. A slim navy header band carries the
 * lifebuoy mark and "Ask DVA"; width is min(760px, 50vw) from 1280 up, full width
 * below. Owns Escape, body scroll lock, initial focus and focus return.
 */
export function HelpOverlayShell({
  open,
  contextLabel,
  parentLabel = null,
  onParentClick,
  onClose,
  faqPane,
  chatPane,
}: HelpOverlayShellProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement;
    panelRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  const chip = (
    <span className="rounded-full bg-white/15 px-[11px] py-1 text-[11.5px] font-semibold text-white">{contextLabel}</span>
  );

  return (
    <div className="fixed inset-0 z-50">
      <div
        aria-hidden="true"
        onClick={onClose}
        className="absolute inset-0 bg-dvaNavy/[0.16] animate-[dvaHelpBackdropIn_200ms_ease-out]"
      />

      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={`Ask DVA — help for ${contextLabel}`}
        className="absolute inset-y-0 right-0 flex w-full min-w-0 flex-col bg-white shadow-[-24px_0_56px_rgba(24,54,94,0.20)] outline-none animate-[dvaHelpPanelIn_220ms_cubic-bezier(.22,1,.36,1)] xl:w-[min(760px,50vw)]"
      >
        <header className="flex h-[52px] shrink-0 items-center gap-[11px] bg-dvaNavy pl-4 pr-2.5">
          <LifeBuoy className="size-5 shrink-0 text-white" strokeWidth={1.9} aria-hidden="true" />
          <span className="text-[15px] font-bold tracking-[-0.1px] text-white">Ask DVA</span>
          <span className="block h-4 w-px bg-white/[0.28]" />

          {parentLabel ? (
            <nav aria-label="Help context" className="flex min-w-0 items-center gap-2">
              <button
                type="button"
                onClick={onParentClick}
                className="shrink-0 border-b border-white/35 pb-px text-xs font-semibold text-white/[0.82] hover:text-white focus-visible:rounded focus-visible:ring-[3px] focus-visible:ring-dvaOrange/35 focus-visible:outline-none"
              >
                {parentLabel}
              </button>
              <ChevronRight className="size-[13px] shrink-0 text-white/50" strokeWidth={2.2} aria-hidden="true" />
              <span aria-current="page" className="truncate">{chip}</span>
            </nav>
          ) : (
            chip
          )}

          <span className="grow" />

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-transparent hover:bg-white/10 focus-visible:ring-[3px] focus-visible:ring-dvaOrange/35 focus-visible:outline-none xl:size-8"
          >
            <X className="size-4 text-white" strokeWidth={2.2} aria-hidden="true" />
          </button>
        </header>

        <div className="flex min-h-0 flex-1 flex-col xl:flex-row xl:items-stretch">
          <div className="shrink-0 border-b border-[#e5e7eb] bg-white xl:w-[300px] xl:border-b-0 xl:border-r">{faqPane}</div>
          <div className="flex min-h-0 flex-1 flex-col bg-dvaNavy/[0.03]">{chatPane}</div>
        </div>
      </div>
    </div>
  );
}
